/* eslint-disable max-nested-callbacks */
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import { LocalFilterOptions } from 'src/components/organisms';
import {
  DonutAndBarChartDataType,
  LineChartDataType,
  useCreateAuditEntryMutation,
} from 'src/graphql';
import { isDownloadType, roundPercentages, saveData } from 'src/utils';
import { CHART_IDS } from 'src/utils/analytics';
import { isBefore } from 'src/utils/time';
import * as Types from '../../../../types';

export const useOrganisationalHealthDownloads = (
  categoriesOverTime: {
    lineChartData?: LineChartDataType[];
    dateGroup: 'Month' | 'Quarter';
  },
  categoriesByDepartment?: DonutAndBarChartDataType[],
) => {
  const features = useSelector((state: Types.State) => state.features);
  const organisation = useSelector(
    (state: Types.State) => state.organisation?.organisation,
  );
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const createAuditEntryMutation = useCreateAuditEntryMutation();

  const downloadCategoriesOverTime = useCallback(
    (
      label: string | number,
      selectedLocalFilters?: LocalFilterOptions,
      print?: () => void,
    ) => {
      const categories: Record<string, string | number>[] = [];
      const groupCategoriesByTimePeriod: Record<
        string,
        Record<string, Record<string, string | number>>
      > = {};

      categoriesOverTime.lineChartData?.forEach(({ category, linePlot }) => {
        linePlot.forEach(({ month: date, count }) => {
          const [month, year] = date.split(' ');
          if (month in groupCategoriesByTimePeriod) {
            if (year in groupCategoriesByTimePeriod[month]) {
              groupCategoriesByTimePeriod[month][year][category] = count;
            } else {
              groupCategoriesByTimePeriod[month][year] = {
                [category]: count,
              };
            }
          } else {
            groupCategoriesByTimePeriod[month] = {
              [year]: {
                [category]: count,
              },
            };
          }
        });
      });
      Object.keys(groupCategoriesByTimePeriod).forEach((month) => {
        Object.keys(groupCategoriesByTimePeriod[month]).forEach((year) => {
          const collected: Record<string, string | number> =
            categoriesOverTime?.dateGroup === 'Month'
              ? {
                  Month: month,
                  Year: year,
                }
              : {
                  Quarter: month,
                  Year: year,
                };
          Object.keys(groupCategoriesByTimePeriod[month][year]).forEach(
            (category) => {
              collected[category] =
                groupCategoriesByTimePeriod[month][year][category];
            },
          );
          categories.push(collected);
        });
      });

      categories.sort((current, next) => {
        const currentYear = `${current.Month}-${current.Year}`;
        const nextYear = `${next.Month}-${next.Year}`;

        if (isBefore(currentYear, nextYear)) {
          return -1;
        }
        return 1;
      });

      if (isDownloadType(label)) {
        saveData(
          label,
          organisation,
          segmentEnabled,
          selectedLocalFilters,
          {
            dashboard: 'organisational',
            title: 'Reported issue categories over time',
            data: categories,
            svgId: CHART_IDS.categories_over_time,
          },
          createAuditEntryMutation,
          print,
        );
      }
    },
    [
      categoriesOverTime?.dateGroup,
      categoriesOverTime.lineChartData,
      createAuditEntryMutation,
      organisation,
      segmentEnabled,
    ],
  );

  const downloadCategoriesByDepartment = useCallback(
    (
      label: string | number,
      selectedLocalFilters?: LocalFilterOptions,
      print?: () => void,
    ) => {
      const remappedData =
        categoriesByDepartment?.flatMap((department) => {
          const total = department.categories.reduce((a, b) => a + b.total, 0);
          const percentages = roundPercentages(
            department.categories.map((category) =>
              total > 0 ? (category.total / total) * 100 : 0,
            ),
          );
          return department.categories.map((category, index) => ({
            'Department name': department.donutSegment,
            'Issue category name': category.category,
            'Issue category count': category.total,
            'Department percentage': `${percentages[index]}%`,
          }));
        }) ?? [];

      if (isDownloadType(label)) {
        saveData(
          label,
          organisation,
          segmentEnabled,
          selectedLocalFilters,
          {
            dashboard: 'organisational',
            title: 'Issue categories by department',
            data: remappedData,
            svgId: CHART_IDS.categories_per_department,
          },
          createAuditEntryMutation,
          print,
        );
      }
    },
    [
      categoriesByDepartment,
      createAuditEntryMutation,
      organisation,
      segmentEnabled,
    ],
  );

  return { downloadCategoriesOverTime, downloadCategoriesByDepartment };
};
